/**
 * wireMasks — the ONE view⇄wire serializer both authoring surfaces use.
 *
 * LB-1. `toWireMasks` was copy-pasted into `TemplateEditor.tsx` and `Wizard.tsx` and had already
 * drifted: the editor's copy carried `corner_radius`, the wizard's dropped it, so a cutout radius
 * authored in the wizard was lost on the first save. Both copies also emitted a `print_area` mask
 * for an EMPTY polygon, and the cloud rejects that per view (`_area([]) <= 1e-9` → `empty_area`)
 * and `db.rollback()`s the WHOLE submit — so adding a view in the editor to mask later made every
 * Publish fail with a 422 that named no view and threw away the sibling views that were correct.
 *
 * One module, one rule: an empty print area emits NO print_area mask (matching the wizard's
 * `masks: []` for an unmasked extra view), and `validateViewsForPublish` refuses the Publish
 * locally, naming the view, before anything is sent.
 */
import type { Mask, Polygon, WireMask, WireView } from "@genz/engine-bind";
import type { ProductTemplate } from "@genz/platform";
import { normalizeMasksForWire } from "@genz/engine-bind";
import { VIEW_LABEL } from "./views.js";

/** Canonical wire polygon ([[x,y]]) → editor polygon ({x,y}[]). */
export const fromWirePolygon = (p: WireMask["polygon"]): Polygon => p.map(([x, y]) => ({ x, y }));
/** Editor polygon ({x,y}[]) → canonical wire polygon ([[x,y]]). */
export const toWirePolygon = (p: Polygon): [number, number][] => p.map((pt) => [pt.x, pt.y]);

/**
 * Build the cloud `MaskShape[]` from the editor's print-area + cutouts (print first).
 *
 * P2-2: a cutout's `cornerRadius` rides along as `corner_radius` so the authored radius survives
 * the round trip (only when set — a square cutout writes no key, keeping old records
 * byte-identical). `Mask.cornerRadius` is the slider value; the rounded polygon is DERIVED from
 * it, so dropping the field brought a reopened template back with the rounded polygon and a
 * radius of 0.
 */
export function toWireMasks(printArea: Polygon, cutouts: Mask[]): WireMask[] {
  const masks: WireMask[] = [];
  // LB-1: an empty print area is NOT `polygon: []` on the wire — it is no mask at all.
  if (printArea.length > 0) masks.push({ kind: "print_area", polygon: toWirePolygon(printArea) });
  for (const c of cutouts) {
    masks.push({
      kind: c.kind ?? "camera_cutout",
      polygon: toWirePolygon(c.polygon),
      ...(typeof c.cornerRadius === "number" && c.cornerRadius > 0
        ? { corner_radius: c.cornerRadius }
        : {}),
    });
  }
  return masks;
}

/**
 * A view's masks in the CANONICAL `{kind, polygon}` spelling.
 *
 * F-4: records written before that spelling carry `mask_type` + a rect `geometry`, and the
 * cloud never rewrites what is already stored. Reading `m.kind` straight through made a
 * legacy template open with an EMPTY print area (nothing matched `kind === "print_area"`) and
 * then made `validateViewsForPublish` refuse the Publish as "no print area" — for a template
 * that has one. `normalizeMasksForWire` is the SAME normaliser the version PATCH
 * (`@genz/platform`) and the render adapter (`@genz/engine-bind`) use; three read/write seams,
 * one rule, no second copy.
 */
function canonicalMasks(view?: WireView): WireMask[] {
  return (normalizeMasksForWire(view?.masks ?? []) ?? []) as WireMask[];
}

/** Pull the print-area polygon + cutout masks out of a single wire view (B4: any view, not
 *  just back). Undefined view / no masks ⇒ empty geometry (a fresh, unmasked view). */
export function geometryFromView(view?: WireView): { printArea: Polygon; cutouts: Mask[] } {
  const masks = canonicalMasks(view);
  const printMask = masks.find((m) => m.kind === "print_area");
  const printArea = printMask ? fromWirePolygon(printMask.polygon) : [];
  const cutouts: Mask[] = masks
    .filter((m) => m.kind !== "print_area")
    .map((m) => {
      // P2-2: read the radius back when the record carries one. Guarded for its ABSENCE — every
      // template saved before the field existed simply keeps `cornerRadius` undefined (= square).
      const radius = m.corner_radius;
      return {
        kind: m.kind,
        polygon: fromWirePolygon(m.polygon),
        ...(typeof radius === "number" ? { cornerRadius: radius } : {}),
      };
    });
  return { printArea, cutouts };
}

/** Write the canvas's current print-area + cutouts (and any freshly-swapped reference) into ONE
 *  view's draft, returning a NEW drafts array (pure). Every OTHER view's draft round-trips
 *  field-for-field. This is how `stashActiveView` + Save capture uncommitted canvas edits without
 *  disturbing the sibling views.
 *
 *  WS-EXIF: `baseImageSize` is the decoded pixel size of the photo the geometry above was drawn
 *  on, stamped onto the view so a later reopen can tell whether the stored 0..1 numbers still
 *  mean anything (see `authoringFrame.ts`). It is written ONLY for the view being stashed and
 *  only when the photo has actually decoded — a null size leaves whatever the record already
 *  carries alone, so a failed image load never erases a good frame. A LEGACY view is therefore
 *  stamped on the operator's next Save, which is exactly when the frame is known to be right. */
export function stashViewInto(
  drafts: WireView[],
  view: string,
  printArea: Polygon,
  cutouts: Mask[],
  brandArea: Polygon | null,
  refUrl?: string,
  baseImageSize?: { w: number; h: number } | null,
): WireView[] {
  return drafts.map((d) =>
    d.view_type === view
      ? {
          ...withoutMarkOnceDrawn(d, printArea),
          masks: toWireMasks(printArea, cutouts),
          // P2-3: the brand-logo quad is per-view state like the masks. `null` (not "omit") when
          // the box is off, so unchecking it actually CLEARS the persisted placement instead of
          // letting the previous quad round-trip forever.
          brand_logo_placement: brandArea ? toWirePolygon(brandArea) : null,
          ...(refUrl ? { base_case_image_ref: refUrl } : {}),
          ...(baseImageSize
            ? { base_image_size: [baseImageSize.w, baseImageSize.h] as [number, number] }
            : {}),
        }
      : d,
  );
}

/** A mock-up only view with a print area drawn on it is a normal view from now on. */
function withoutMarkOnceDrawn(d: WireView, printArea: Polygon): WireView {
  if (printArea.length === 0 || !d.mockup_only) return d;
  const { mockup_only: _drawn, ...rest } = d;
  return rest;
}

/** A "Make template" mock-up: the list row says so, or (a synced record) its payload does. */
export function isMockupOnlyTemplate(t: ProductTemplate): boolean {
  return t.mockupOnly ?? (t.currentVersion?.payload?.views ?? []).some((v) => v.mockup_only);
}

/** One view that cannot be published, and why. */
export interface ViewProblem {
  view: string;
  problem: string;
}

/** Every view Publish must refuse, in `views` order. Mirrors the cloud's per-view `_invalid_views`
 *  check (`cloud/app/catalog/service.py`) for the one rule the client can evaluate locally: a view
 *  with no print-area polygon is `empty_area` server-side, and a rejected submit rolls ALL views
 *  back — so it has to be caught before anything is sent. A `mockup_only` view may have none. */
export function validateViewsForPublish(views: WireView[]): ViewProblem[] {
  return views
    .filter((v) => !v.mockup_only)
    // F-4: normalised too — a legacy view HAS a print area, it just spells it differently, and
    // refusing its Publish would be the same lie `geometryFromView` used to tell the canvas.
    .filter((v) => !canonicalMasks(v).some((m) => m.kind === "print_area" && (m.polygon?.length ?? 0) > 0))
    .map((v) => ({ view: v.view_type, problem: "no print area" }));
}

/** "Front and Left" — an Oxford-free list of human view names. */
function nameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The operator-facing sentence for what `validateViewsForPublish` found. Names the view(s) —
 *  the whole point of LB-1 is that the old message named none, so the operator (standing on a
 *  tab that IS valid) had nothing to act on. */
export function viewProblemsMessage(problems: ViewProblem[]): string {
  const names = problems.map((p) => VIEW_LABEL[p.view] ?? p.view);
  if (names.length === 1) return `${names[0]} view has no print area — draw it or remove the view.`;
  return `${nameList(names)} views have no print area — draw them or remove them.`;
}

/** The cloud's per-view 422 body (`cloud/app/catalog/router.py` — `{errors, views: {front: [...]}}`)
 *  turned into the same sentence the local guard uses. The server goes out of its way to name the
 *  offending view; returning null here (not a canned string) lets the caller fall back only when
 *  there is genuinely nothing to name. */
export function serverViewErrorMessage(body: unknown): string | null {
  const detail = (body as { detail?: unknown } | undefined)?.detail;
  const views = (detail as { views?: unknown } | undefined)?.views;
  if (!views || typeof views !== "object") return null;
  const entries = Object.entries(views as Record<string, unknown>);
  if (entries.length === 0) return null;
  const codes = entries.flatMap(([, v]) => (Array.isArray(v) ? v.map(String) : []));
  if (codes.length > 0 && codes.every((c) => c === "empty_area")) {
    return viewProblemsMessage(entries.map(([view]) => ({ view, problem: "no print area" })));
  }
  const names = nameList(entries.map(([view]) => VIEW_LABEL[view] ?? view));
  const noun = entries.length === 1 ? "view" : "views";
  return `${names} ${noun} failed server validation: ${[...new Set(codes)].join(", ")}.`;
}
